import { type ServiceSummary, ServicesResult } from '@apm/contracts';
import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { usePolling } from '../hooks/usePolling';
import { type QueryState, useQuery } from '../hooks/useQuery';

const REFRESH_MS = 30_000;

const EMPTY: QueryState<{
  at: number;
  windowMs: number;
  rows: ServiceSummary[];
}> = {
  loading: false,
  stale: false,
  reload: () => {},
};

const ServicesContext = createContext(EMPTY);

/**
 * `/api/services`를 화면 전체에서 한 번만 읽고 30초마다 갱신한다.
 * 탭이 숨은 동안은 쉬고, 다시 보이면 곧바로 읽는다 (44회차, `usePolling`)
 */
export function ServiceProvider({ children }: { children: ReactNode }) {
  const version = usePolling(REFRESH_MS);
  const query = useQuery('/api/services', ServicesResult, version);
  // `useQuery`는 렌더마다 새 객체를 준다. 칸 기준으로 메모해야 30초 폴링의 다시 읽기 사이에
  // 같은 값으로 구독자 전체(화면 전부)를 깨우지 않는다 (D-C8)
  const { loading, stale, data, error, reload } = query;
  const value = useMemo(
    () => ({ loading, stale, data, error, reload }),
    [loading, stale, data, error, reload],
  );
  return <ServicesContext.Provider value={value}>{children}</ServicesContext.Provider>;
}

export function useServices() {
  return useContext(ServicesContext);
}
